import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import HeroCarousel from "@/components/HeroCarousel";
import AdVideos from "@/components/AdVideos";
import CategoryBar from "@/components/CategoryBar";
import VariantSheet from "@/components/VariantSheet";
import api from "@/lib/api";

// Build a slug → slug alias map at runtime from the live categories list.
// This lets the smart-search match on partial category names ("pickle" → "pickles",
// "atta" → "flours" etc.) without hardcoding a static dictionary. Aliases derived
// from each category's name/slug are added automatically for every category the
// super admin creates in the Categories tab.
function buildCategoryAliases(cats) {
  const aliases = {};
  for (const c of cats || []) {
    if (!c?.slug) continue;
    const slug = c.slug.toLowerCase();
    aliases[slug] = slug;
    // Name-based aliases (lowercased, whole + singular).
    const name = (c.name || "").trim().toLowerCase();
    if (name) {
      aliases[name] = slug;
      if (name.endsWith("s")) aliases[name.slice(0, -1)] = slug;
    }
    // slug-based aliases (with & without dashes, singular).
    aliases[slug.replace(/-/g, " ")] = slug;
    if (slug.endsWith("s")) aliases[slug.slice(0, -1)] = slug;
  }
  return aliases;
}

// Extract a size/pack token from a search query if present. Returns { size, cleaned }.
// Uses a permissive regex — any digit+unit combo (35g, 60g, 500ml, 1kg, 1L, 250ml, …)
// or ₹ amount is picked up. Actual size-availability is checked against product data,
// so new sizes created in the admin are respected automatically.
const SIZE_RE = /\b(\d+\s?(?:g|kg|ml|l))\b|(₹\s?\d+)/i;
function extractSize(q) {
  const m = q.match(SIZE_RE);
  if (!m) return { size: null, cleaned: q };
  const raw = (m[1] || m[2] || "").replace(/\s+/g, "").toLowerCase();
  // Common label capitalisation used by our catalogue (1Kg / 1L / 500ml).
  const NORM_UNIT = raw.replace(/(\d)kg$/i, "$1Kg").replace(/(\d)l$/i, "$1L");
  const cleaned = q.replace(m[0], " ").replace(/\s+/g, " ").trim();
  return { size: NORM_UNIT || raw, cleaned };
}

// Given a full raw query and the loaded catalogue, decide what the user meant.
// Returns { category, product, size, sizeAvailable, note }.
function parseSearchQuery(rawQuery, allProducts, aliases) {
  const raw = (rawQuery || "").trim();
  if (!raw) return { category: null, product: null, size: null, sizeAvailable: null, note: null };

  const { size, cleaned } = extractSize(raw);
  const remainder = cleaned.toLowerCase();

  // 1) Try exact/alias category match on the (possibly cleaned) text.
  let category = null;
  let categoryOnly = false;   // true → user typed only a category name, skip product match.
  if (remainder && aliases[remainder]) {
    category = aliases[remainder];
    categoryOnly = true;
  }
  if (!category) {
    for (const key of Object.keys(aliases)) {
      if (remainder === key) { category = aliases[key]; categoryOnly = true; break; }
    }
  }
  if (!category) {
    for (const key of Object.keys(aliases)) {
      if (key && remainder.includes(key)) { category = aliases[key]; break; }
    }
  }

  // 2) Product match — skip when the user typed only a category alias.
  //    Case-insensitive contains, ranked by earliest match position. When a size
  //    is provided, prefer the product doc whose variants include that size
  //    (our schema stores one doc per name × weight).
  let product = null;
  if (!categoryOnly && remainder && allProducts.length) {
    const needle = remainder;
    const hits = allProducts
      .filter((p) => p.variants && p.variants.length)
      .map((p) => ({ p, idx: p.name.toLowerCase().indexOf(needle) }))
      .filter((x) => x.idx !== -1)
      .sort((a, b) => a.idx - b.idx || a.p.name.length - b.p.name.length);
    if (hits.length) {
      if (size) {
        const withSize = hits.find(({ p }) => (p.variants || []).some((v) => v.label.toLowerCase() === size.toLowerCase()));
        product = withSize ? withSize.p : hits[0].p;
      } else {
        product = hits[0].p;
      }
    }
  }

  // 3) If a size was requested but the matched product doesn't have it, fall back to the
  //    closest available size and flag it. If no size was requested, default to the product's
  //    first variant so the popup can auto-open.
  let resolvedSize = size;
  let sizeAvailable = null;
  if (product) {
    if (size) {
      const has = (product.variants || []).some((v) => v.label.toLowerCase() === size.toLowerCase());
      sizeAvailable = has;
      if (!has) resolvedSize = (product.variants || [])[0]?.label || null;
    } else {
      resolvedSize = (product.variants || [])[0]?.label || null;
    }
  }

  // 4) If we matched a product, use its category. If we matched only category (no product), keep it.
  if (product) category = product.category;

  return { category, product, size: resolvedSize, sizeAvailable, note: null };
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState("");
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [weightReps, setWeightReps] = useState({});  // { "pickles|200g": {image_url, name?} }
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetSize, setSheetSize] = useState(null);
  const [query, setQuery] = useState("");
  const [headerH, setHeaderH] = useState(112);
  const productsRef = useRef(null);
  const scrolledForRef = useRef("");
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    api.get("/categories").then((r) => {
      const arr = r.data || [];
      setCategories(arr);
      // Default the selected category to the first live category once the list
      // arrives (so brand-new categories added by admin work out-of-the-box).
      setSelected((cur) => cur || arr[0]?.slug || "");
    }).catch(() => {});
    api.get("/media?kind=ad_video").then((r) => setVideos(r.data.map((m) => m.url))).catch(() => {});
    api.get("/weight-reps").then((r) => {
      const m = {};
      r.data.forEach((x) => { m[`${x.category}|${x.weight_label}`] = x; });
      setWeightReps(m);
    }).catch(() => {});
    Promise.all([
      api.get("/products").then((r) => setAllProducts(r.data)).catch(() => {}),
    ]);
    // Always land at the top on first page load — beat any hash / browser-restore behaviour.
    if (!loc.search) {
      requestAnimationFrame(() => window.scrollTo(0, 0));
      setTimeout(() => window.scrollTo(0, 0), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track header height dynamically for sticky category bar attachment.
  useEffect(() => {
    const el = document.querySelector('header[data-testid="site-header"]');
    if (!el) return;
    const update = () => setHeaderH(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("scroll", update, { passive: true });
    return () => { ro.disconnect(); window.removeEventListener("scroll", update); };
  }, []);

  const scrollToProducts = () => {
    const el = productsRef.current;
    if (!el) return;
    // Use current header height at click time — captured via ref-less read.
    const currentHeader = document.querySelector('header[data-testid="site-header"]')?.offsetHeight ?? headerH;
    const y = el.getBoundingClientRect().top + window.scrollY - (currentHeader + 24);
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const [sheetInitialProduct, setSheetInitialProduct] = useState(null);

  // URL param → state (smart search: category jump / product+size popup auto-open).
  useEffect(() => {
    // Wait for the catalogue before parsing — otherwise product/category detection is
    // impossible and we'd fire a spurious "no products match" toast.
    if (!allProducts.length) return;
    const params = new URLSearchParams(loc.search);
    const cat = params.get("cat");
    const pidParam = params.get("pid");
    const sizeParam = params.get("size");
    const q = params.get("q") || "";
    setQuery(""); // Search query is consumed once — don't keep filtering the storefront list.

    let nextCat = null;
    let openProduct = null;
    let openSize = null;

    const categorySlugs = categories.map((c) => c.slug);
    if (cat && categorySlugs.includes(cat)) {
      nextCat = cat;
    }

    // Direct product+size deep-link (used by the live search dropdown) — takes
    // priority over the text-parsing branch below.
    if (pidParam) {
      const p = allProducts.find((x) => x.id === pidParam);
      if (p) {
        openProduct = p;
        const hasSize = sizeParam && (p.variants || []).some((v) => v.label === sizeParam);
        openSize = hasSize ? sizeParam : (p.variants || [])[0]?.label || null;
        nextCat = p.category;
      }
    }

    if (!openProduct && q) {
      const aliases = buildCategoryAliases(categories);
      const res = parseSearchQuery(q, allProducts, aliases);
      if (res.category) nextCat = res.category;
      if (res.product) {
        openProduct = res.product;
        openSize = res.size;
        if (res.size && res.sizeAvailable === false) {
          import("sonner").then(({ toast }) => {
            const fallback = openProduct.variants?.[0]?.label || "the available size";
            toast(`${q} — size not available, showing ${fallback} instead.`, { duration: 3500 });
          });
        }
      } else if (!res.category) {
        import("sonner").then(({ toast }) => toast(`No products match "${q}".`, { id: `nomatch-${q}`, duration: 3000 }));
      }
    }

    if (nextCat) setSelected(nextCat);

    const key = `${nextCat || ""}|${q}|${pidParam || ""}|${sizeParam || ""}|${openProduct?.id || ""}`;
    if ((nextCat || q) && scrolledForRef.current !== key) {
      scrolledForRef.current = key;
      setTimeout(scrollToProducts, 250);
    }

    // Auto-open the variant popup if we found a specific product.
    if (openProduct && openSize) {
      setTimeout(() => {
        setSheetInitialProduct(openProduct);
        setSheetSize(openSize);
        setSheetOpen(true);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.search, allProducts, categories]);

  // Filter products locally from the already-fetched full catalogue — zero extra network.
  useEffect(() => {
    if (!allProducts.length) return;
    setProducts(allProducts.filter((p) => p.category === selected));
    setLoading(false);
  }, [selected, allProducts]);

  const category = useMemo(() => categories.find((c) => c.slug === selected), [categories, selected]);
  const orderedSizes = category?.sizes || [];

  // For each weight, find flavours available at that weight + pick representative image.
  const weightSections = useMemo(() => {
    return orderedSizes.map((sz) => {
      const flavours = products
        .map((p) => ({ product: p, variant: p.variants.find((v) => v.label === sz) }))
        .filter((x) => !!x.variant);
      const rep = weightReps[`${selected}|${sz}`];
      const defaultFlavour = flavours.find((f) => f.product.name === category?.default_product_name) || flavours[0];
      const image_url = rep?.image_url || defaultFlavour?.variant?.image_url || defaultFlavour?.product?.image_url;
      const from_price = flavours.length ? Math.min(...flavours.map((f) => f.variant.price)) : 0;
      return {
        size: sz,
        flavours,
        image_url,
        rep_name: rep?.name || `${sz} ${category?.name || ""}`,
        count: flavours.length,
        from_price,
      };
    }).filter((s) => s.flavours.length > 0);
  }, [orderedSizes, products, weightReps, selected, category]);

  const filteredSections = useMemo(() => {
    if (!query.trim()) return weightSections;
    const q = query.toLowerCase();
    return weightSections
      .map((s) => ({ ...s, flavours: s.flavours.filter((x) => x.product.name.toLowerCase().includes(q)) }))
      .filter((s) => s.flavours.length > 0);
  }, [weightSections, query]);

  const handleCategoryChange = (slug) => {
    setSelected(slug);
    const params = new URLSearchParams();
    params.set("cat", slug);
    nav(`/?${params.toString()}`, { replace: true });
    // The URL-param useEffect above handles the (deduped) scroll for us.
  };

  const openSheet = (size) => { setSheetSize(size); setSheetOpen(true); };

  return (
    <div className="App bg-ivory min-h-screen">
      <Header />

      <main style={{ paddingTop: `${headerH}px` }}>
        <HeroCarousel />
        <AdVideos videos={videos} />
        <CategoryBar selected={selected} onSelect={handleCategoryChange} productsRef={productsRef} headerHeight={headerH} />

        <section
          ref={productsRef}
          data-testid="product-browsing"
          className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-8 md:py-12"
        >
          <h1
            data-testid="category-heading"
            className="text-brown font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight uppercase"
          >
            {query ? (
              `Search · ${query}`
            ) : (
              <>
                {category?.name || selected}{" "}
                <span
                  data-testid="category-heading-suffix"
                  className="normal-case"
                >
                  by Sizes
                </span>
              </>
            )}
          </h1>
          <p className="mt-2 text-brown-medium text-sm">
            Pick a weight first, then choose from all flavours available in that pack size.
          </p>

          <div className="mt-8 space-y-6 md:space-y-8">
            {loading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse flex flex-col md:flex-row gap-4 bg-white border border-brown-border">
                  <div className="w-full md:w-64 aspect-square bg-ivory-soft" />
                  <div className="flex-1 p-5 space-y-3">
                    <div className="h-6 bg-ivory-soft w-1/3" />
                    <div className="h-4 bg-ivory-soft w-2/3" />
                    <div className="h-12 bg-ivory-soft rounded-full w-1/3" />
                  </div>
                </div>
              ))
            ) : filteredSections.length === 0 ? (
              <div className="text-center py-16 text-brown-medium">
                <p className="text-xl font-semibold">No products in this category yet.</p>
                <p className="text-sm mt-2">Please check back soon.</p>
              </div>
            ) : (
              filteredSections.map((s) => (
                <WeightRow
                  key={s.size}
                  category={category}
                  section={s}
                  onOpen={() => openSheet(s.size)}
                />
              ))
            )}
          </div>
        </section>
      </main>

      <Footer />

      <VariantSheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) setSheetInitialProduct(null);
        }}
        products={products}
        initialProduct={sheetInitialProduct}
        size={sheetSize}
        sizes={orderedSizes}
        categoryName={category?.name || ""}
      />
    </div>
  );
}

function WeightRow({ category, section, onOpen }) {
  const flavourNames = section.flavours.slice(0, 5).map((f) => f.product.name).join(" · ");
  return (
    <article
      data-testid={`weight-section-${section.size}`}
      className="flex flex-row bg-white border border-brown-border rounded-2xl overflow-hidden group"
    >
      <button
        type="button"
        data-testid={`weight-image-${section.size}`}
        onClick={onOpen}
        aria-label={`Open ${section.size} ${category?.name || ""} flavours`}
        className="w-28 sm:w-36 md:w-48 aspect-square bg-ivory-soft overflow-hidden flex-shrink-0 relative cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red"
      >
        {section.image_url ? (
          <img
            src={section.image_url}
            alt={`${section.size} ${category?.name || ""}`}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.06]"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-brown-light text-xs">No image</div>
        )}
      </button>

      <div className="flex-1 p-3 sm:p-4 md:p-5 flex flex-col justify-between min-w-0">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-brand-red font-semibold">
            {category?.name}
          </div>
          <h2 data-testid={`weight-heading-${section.size}`} className="mt-0.5 text-brown font-bold text-xl md:text-2xl">
            {section.size}
          </h2>
          <div className="mt-1 text-brown-medium text-xs md:text-sm">
            {section.count} {section.count === 1 ? "flavour" : "flavours"} · from ₹{section.from_price}
          </div>
          <p className="mt-1.5 text-brown/75 text-xs md:text-sm line-clamp-2">
            {flavourNames}
            {section.flavours.length > 5 ? " · +more" : ""}
          </p>
        </div>

        <div className="mt-3">
          <button
            data-testid={`weight-choose-variant-${section.size}`}
            onClick={onOpen}
            className="inline-flex items-center h-9 md:h-10 px-5 md:px-6 rounded-full bg-brand-red text-ivory font-semibold text-[11px] md:text-[12px] tracking-[0.14em] uppercase hover:bg-brand-red-dark transition-colors shadow-[0_2px_10px_-4px_rgba(200,42,42,0.5)]"
          >
            Choose Flavour
          </button>
        </div>
      </div>
    </article>
  );
}
